import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader, StatCard } from '../components/common';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Chip,
  Tabs,
  Tab,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  FormControlLabel,
  Alert,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
// DataGrid not needed on this page currently
import Grid from '@mui/material/Grid';
import {
  Edit,
  Add,
  Delete,
  Save,
  Cancel,
  CameraAlt,
  Language,
  LocationOn,
  Phone,
  Email,
  Security,
  Facebook,
  X as XIcon,
  Instagram,
  LinkedIn,
  YouTube,
  Settings,
  Draw,
  TextFields,
  Upload,
  Clear,
  AccountBox,
  ViewModule,
  MiscellaneousServices,
  Receipt,
  EventBusy,
  EventAvailable,
  QuestionAnswer,
  ContactPhone,
} from '@mui/icons-material';
import {
  countyProfileApi,
  County,
  CountySignatory,
  Faq,
  CountyContact,
  ContactType,
} from '../api/countyProfile';
import { useSnackbar } from '../contexts/SnackbarContext';
import { usePermission, useCountyBillingStatus } from '../hooks';
import { CountySettlementsPanel } from '../components/billing/CountySettlementsPanel';
import apiClient from '../api/client';
import { colors } from '../theme/theme';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// ==================== Main Page ====================
export const CountyProfilePage = () => {
  const { can } = usePermission();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(0);
  const [makerCheckerEnabled, setMakerCheckerEnabled] = useState(false);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [signatoryDialogOpen, setSignatoryDialogOpen] = useState(false);
  const [editingSignatory, setEditingSignatory] = useState<CountySignatory | null>(null);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [selectedSignatory, setSelectedSignatory] = useState<CountySignatory | null>(null);

  // Combined profile + settings form
  const [profileForm, setProfileForm] = useState({
    name: '',
    address: '', phone: '', email: '', website: '',
    tagline: '', theme_color: '',
    business_hours: '', about: '', footer_blurb: '',
    facebook: '', twitter: '', instagram: '', linkedin: '', youtube: '',
  });
  const [originalProfileForm, setOriginalProfileForm] = useState({ ...profileForm });

  // FAQ dialog state
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState<Faq | null>(null);
  const [faqForm, setFaqForm] = useState({ question: '', answer: '' });

  // Contact dialog state
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<CountyContact | null>(null);
  const [contactForm, setContactForm] = useState<{ contact_id: number | ''; value: string }>({ contact_id: '', value: '' });

  const [signatoryForm, setSignatoryForm] = useState({ name: '', title: '', signature: '', is_active: true });
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [signatureMode, setSignatureMode] = useState<'draw' | 'type' | 'upload'>('upload');
  const [signatureText, setSignatureText] = useState('');
  const [signatureFont, setSignatureFont] = useState('Brush Script MT');
  const canvasRef = useState<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Queries
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['countyProfile'],
    queryFn: () => countyProfileApi.getProfile(),
  });

  const billing = useCountyBillingStatus();

  const { data: signatories, isLoading: signatoriesLoading } = useQuery({
    queryKey: ['countySignatories'],
    queryFn: () => countyProfileApi.getSignatories(),
  });

  const { data: settings } = useQuery({
    queryKey: ['countySettings'],
    queryFn: () => countyProfileApi.getSettings(),
  });

  const { data: modules } = useQuery({
    queryKey: ['countyModules'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/county/modules?per_page=100');
      const raw = res.data as any;
      return Array.isArray(raw) ? raw : (raw?.data || []);
    },
  });

  const { data: faqs, isLoading: faqsLoading } = useQuery({
    queryKey: ['countyFaqs'],
    queryFn: () => countyProfileApi.getFaqs(),
  });

  const { data: contacts, isLoading: contactsLoading } = useQuery({
    queryKey: ['countyContacts'],
    queryFn: () => countyProfileApi.getContacts(),
  });

  const { data: contactTypes } = useQuery({
    queryKey: ['countyContactTypes'],
    queryFn: () => countyProfileApi.getContactTypes(),
  });

  // Mutations
  const updateProfileMutation = useMutation({
    mutationFn: (data: Partial<County>) => countyProfileApi.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countyProfile'] });
      showSnackbar('Profile updated successfully!', 'success');
    },
    onError: (error: any) => showSnackbar(error?.response?.data?.message || 'Failed to update profile', 'error'),
  });

  const updateSettingsMutation = useMutation({
    mutationFn: (data: any) => countyProfileApi.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countySettings'] });
      showSnackbar('Settings updated successfully!', 'success');
    },
    onError: (error: any) => showSnackbar(error?.response?.data?.message || 'Failed to update settings', 'error'),
  });

  const toggleMakerCheckerMutation = useMutation({
    mutationFn: (enabled: boolean) => countyProfileApi.toggleMakerChecker(enabled),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['countyProfile'] });
      showSnackbar(variables ? 'Maker-checker enabled!' : 'Maker-checker disabled!', 'success');
    },
    onError: (error: any) => showSnackbar(error?.response?.data?.message || 'Failed to update maker-checker', 'error'),
  });

  const addSignatoryMutation = useMutation({
    mutationFn: (data: any) => countyProfileApi.addSignatory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countySignatories'] });
      setSignatoryDialogOpen(false);
      resetSignatoryForm();
      showSnackbar('Signatory added successfully!', 'success');
    },
    onError: (error: any) => showSnackbar(error?.response?.data?.message || 'Failed to add signatory', 'error'),
  });

  const updateSignatoryMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CountySignatory> }) =>
      countyProfileApi.updateSignatory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countySignatories'] });
      setSignatoryDialogOpen(false);
      resetSignatoryForm();
      showSnackbar('Signatory updated successfully!', 'success');
    },
    onError: (error: any) => showSnackbar(error?.response?.data?.message || 'Failed to update signatory', 'error'),
  });

  const deleteSignatoryMutation = useMutation({
    mutationFn: (id: number) => countyProfileApi.deleteSignatory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countySignatories'] });
      showSnackbar('Signatory deleted successfully!', 'success');
    },
    onError: (error: any) => showSnackbar(error?.response?.data?.message || 'Failed to delete signatory', 'error'),
  });

  const uploadSignatureMutation = useMutation({
    mutationFn: ({ id, signature }: { id: number; signature: string }) =>
      countyProfileApi.uploadSignature(id, signature),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countySignatories'] });
      setSignatureDialogOpen(false);
      setSelectedSignatory(null);
      setLogoPreview('');
      showSnackbar('Signature uploaded successfully!', 'success');
    },
    onError: (error: any) => showSnackbar(error?.response?.data?.message || 'Failed to upload signature', 'error'),
  });

  const updateLogoMutation = useMutation({
    mutationFn: (logo: string) => countyProfileApi.updateLogo(logo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countyProfile'] });
      setLogoDialogOpen(false);
      setLogoPreview('');
      showSnackbar('Logo updated successfully!', 'success');
    },
    onError: (error: any) => showSnackbar(error?.response?.data?.message || 'Failed to update logo', 'error'),
  });

  // FAQ mutations
  const addFaqMutation = useMutation({
    mutationFn: (data: { question: string; answer: string }) => countyProfileApi.createFaq(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countyFaqs'] });
      setFaqDialogOpen(false);
      resetFaqForm();
      showSnackbar('FAQ added successfully!', 'success');
    },
    onError: (error: any) => showSnackbar(error?.response?.data?.message || 'Failed to add FAQ', 'error'),
  });

  const updateFaqMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { question: string; answer: string } }) =>
      countyProfileApi.updateFaq(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countyFaqs'] });
      setFaqDialogOpen(false);
      resetFaqForm();
      showSnackbar('FAQ updated successfully!', 'success');
    },
    onError: (error: any) => showSnackbar(error?.response?.data?.message || 'Failed to update FAQ', 'error'),
  });

  const deleteFaqMutation = useMutation({
    mutationFn: (id: number) => countyProfileApi.deleteFaq(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countyFaqs'] });
      showSnackbar('FAQ deleted successfully!', 'success');
    },
    onError: (error: any) => showSnackbar(error?.response?.data?.message || 'Failed to delete FAQ', 'error'),
  });

  // Contact mutations
  const addContactMutation = useMutation({
    mutationFn: (data: { contact_id: number; value: string }) => countyProfileApi.addContact(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countyContacts'] });
      setContactDialogOpen(false);
      resetContactForm();
      showSnackbar('Contact added successfully!', 'success');
    },
    onError: (error: any) => showSnackbar(error?.response?.data?.message || 'Failed to add contact', 'error'),
  });

  const updateContactMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { contact_id?: number; value?: string } }) =>
      countyProfileApi.updateContact(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countyContacts'] });
      setContactDialogOpen(false);
      resetContactForm();
      showSnackbar('Contact updated successfully!', 'success');
    },
    onError: (error: any) => showSnackbar(error?.response?.data?.message || 'Failed to update contact', 'error'),
  });

  const deleteContactMutation = useMutation({
    mutationFn: (id: number) => countyProfileApi.deleteContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countyContacts'] });
      showSnackbar('Contact deleted successfully!', 'success');
    },
    onError: (error: any) => showSnackbar(error?.response?.data?.message || 'Failed to delete contact', 'error'),
  });

  React.useEffect(() => {
    if (profile && 'maker_checker_enabled' in profile) {
      setMakerCheckerEnabled(profile.maker_checker_enabled === true);
    }
  }, [profile]);

  // Helpers
  const details = (profile as any)?.county_details || (profile as any)?.countyDetails;

  const resetSignatoryForm = () => {
    setSignatoryForm({ name: '', title: '', signature: '', is_active: true });
    setEditingSignatory(null);
    setLogoPreview('');
    setSignatureText('');
    setSignatureMode('upload');
  };

  const handleEditProfile = () => {
    const formValues = {
      name: profile?.name || '',
      address: details?.office_location || '',
      phone: details?.phone || '',
      email: details?.email || '',
      website: details?.website || '',
      tagline: settings?.tagline || '',
      theme_color: settings?.theme_color || '',
      business_hours: settings?.business_hours || '',
      about: settings?.about || '',
      footer_blurb: settings?.footer_blurb || '',
      facebook: settings?.facebook || '',
      twitter: settings?.twitter || '',
      instagram: settings?.instagram || '',
      linkedin: settings?.linkedin || '',
      youtube: settings?.youtube || '',
    };
    setProfileForm(formValues);
    setOriginalProfileForm(formValues);
    setEditDialogOpen(true);
  };

  const handleSaveProfile = async () => {
    try {
      // Save contact info
      await updateProfileMutation.mutateAsync({
        name: profileForm.name,
        address: profileForm.address,
        phone: profileForm.phone,
        email: profileForm.email,
        website: profileForm.website,
      } as any);

      // Save branding + social + content
      await updateSettingsMutation.mutateAsync({
        tagline: profileForm.tagline,
        theme_color: profileForm.theme_color,
        business_hours: profileForm.business_hours,
        about: profileForm.about,
        footer_blurb: profileForm.footer_blurb,
        facebook: profileForm.facebook,
        twitter: profileForm.twitter,
        instagram: profileForm.instagram,
        linkedin: profileForm.linkedin,
        youtube: profileForm.youtube,
      });

      setEditDialogOpen(false);
    } catch {
      // errors handled by mutation onError
    }
  };

  // FAQ handlers
  const resetFaqForm = () => {
    setFaqForm({ question: '', answer: '' });
    setEditingFaq(null);
  };

  const handleOpenFaqDialog = (faq?: Faq) => {
    if (faq) {
      setEditingFaq(faq);
      setFaqForm({ question: faq.question, answer: faq.answer });
    } else {
      resetFaqForm();
    }
    setFaqDialogOpen(true);
  };

  const handleSaveFaq = () => {
    if (editingFaq) {
      updateFaqMutation.mutate({ id: editingFaq.id, data: faqForm });
    } else {
      addFaqMutation.mutate(faqForm);
    }
  };

  const handleDeleteFaq = (id: number) => {
    if (window.confirm('Are you sure you want to delete this FAQ?')) deleteFaqMutation.mutate(id);
  };

  // Contact handlers
  const resetContactForm = () => {
    setContactForm({ contact_id: '', value: '' });
    setEditingContact(null);
  };

  const handleOpenContactDialog = (contact?: CountyContact) => {
    if (contact) {
      setEditingContact(contact);
      setContactForm({ contact_id: contact.contact_id, value: contact.value });
    } else {
      resetContactForm();
    }
    setContactDialogOpen(true);
  };

  const handleSaveContact = () => {
    if (contactForm.contact_id === '') return;
    const contact_id = Number(contactForm.contact_id);
    if (editingContact) {
      updateContactMutation.mutate({ id: editingContact.id, data: { contact_id, value: contactForm.value } });
    } else {
      addContactMutation.mutate({ contact_id, value: contactForm.value });
    }
  };

  const handleDeleteContact = (id: number) => {
    if (window.confirm('Are you sure you want to delete this contact?')) deleteContactMutation.mutate(id);
  };

  const handleOpenSignatoryDialog = (signatory?: CountySignatory) => {
    if (signatory) {
      setEditingSignatory(signatory);
      setSignatoryForm({ name: signatory.name, title: signatory.title, signature: signatory.signature || '', is_active: signatory.is_active });
    } else {
      resetSignatoryForm();
    }
    setSignatoryDialogOpen(true);
  };

  const handleSaveSignatory = () => {
    let signature = signatoryForm.signature;
    if (!signature) {
      if (signatureMode === 'upload' && logoPreview) signature = logoPreview;
      else if (signatureMode === 'type' && signatureText) {
        const canvas = document.createElement('canvas');
        canvas.width = 400; canvas.height = 100;
        const ctx = canvas.getContext('2d');
        if (ctx) { ctx.fillStyle = 'white'; ctx.fillRect(0, 0, 400, 100); ctx.font = `48px ${signatureFont}, cursive`; ctx.fillStyle = 'black'; ctx.textBaseline = 'middle'; ctx.fillText(signatureText, 20, 50); signature = canvas.toDataURL('image/png'); }
      } else if (signatureMode === 'draw' && canvasRef[0]) signature = canvasRef[0].toDataURL('image/png');
    }

    if (editingSignatory) {
      updateSignatoryMutation.mutate({ id: editingSignatory.id, data: { ...signatoryForm, signature } });
    } else {
      addSignatoryMutation.mutate({ ...signatoryForm, signature });
    }
  };

  const handleDeleteSignatory = (id: number) => {
    if (window.confirm('Are you sure you want to delete this signatory?')) deleteSignatoryMutation.mutate(id);
  };

  const handleUploadSignature = (signatory: CountySignatory) => {
    setSelectedSignatory(signatory);
    setSignatureDialogOpen(true);
    setLogoPreview('');
    setSignatureText('');
    setSignatureMode('upload');
  };

  const handleSaveSignature = () => {
    if (!selectedSignatory) return;
    let signature = '';
    if (signatureMode === 'upload' && logoPreview) signature = logoPreview;
    else if (signatureMode === 'type' && signatureText) {
      const canvas = document.createElement('canvas');
      canvas.width = 400; canvas.height = 100;
      const ctx = canvas.getContext('2d');
      if (ctx) { ctx.fillStyle = 'white'; ctx.fillRect(0, 0, 400, 100); ctx.font = `48px ${signatureFont}, cursive`; ctx.fillStyle = 'black'; ctx.textBaseline = 'middle'; ctx.fillText(signatureText, 20, 50); signature = canvas.toDataURL('image/png'); }
    } else if (signatureMode === 'draw' && canvasRef[0]) signature = canvasRef[0].toDataURL('image/png');
    if (signature) uploadSignatureMutation.mutate({ id: selectedSignatory.id, signature });
  };

  const handleClearCanvas = () => {
    if (canvasRef[0]) { const ctx = canvasRef[0].getContext('2d'); if (ctx) { ctx.clearRect(0, 0, canvasRef[0].width, canvasRef[0].height); ctx.fillStyle = 'white'; ctx.fillRect(0, 0, canvasRef[0].width, canvasRef[0].height); } }
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef[0]) return; setIsDrawing(true);
    const rect = canvasRef[0].getBoundingClientRect();
    const ctx = canvasRef[0].getContext('2d');
    if (ctx) { ctx.beginPath(); ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top); }
  };
  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !canvasRef[0]) return;
    const rect = canvasRef[0].getBoundingClientRect();
    const ctx = canvasRef[0].getContext('2d');
    if (ctx) { ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top); ctx.strokeStyle = 'black'; ctx.lineWidth = 2; ctx.stroke(); }
  };
  const stopDrawing = () => setIsDrawing(false);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) { const reader = new FileReader(); reader.onloadend = () => setLogoPreview(reader.result as string); reader.readAsDataURL(file); }
  };

  const currentLogo = details?.logo || `https://ui-avatars.com/api/?name=${profile?.name}&size=120`;

  if (profileLoading || signatoriesLoading) {
    return <AdminLayout><Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box></AdminLayout>;
  }

  const activeModules = Array.isArray(modules) ? modules.filter((m: any) => m.status === 'active').length : 0;
  const totalServices = Array.isArray(modules) ? modules.reduce((sum: number, m: any) => sum + (m.services_count || 0), 0) : 0;

  return (
    <AdminLayout>
      <Box>
        <PageHeader title="County Profile" subtitle="Manage your county information and settings"
          breadcrumbs={[{ label: 'Dashboard', path: '/dashboard' }, { label: 'County Profile' }]}
        />

        {/* Stat Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <StatCard title="Active Modules" value={activeModules} icon={<ViewModule />} color={colors.success.main} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <StatCard title="Signatories" value={Array.isArray(signatories) ? signatories.length : 0} icon={<AccountBox />} color={colors.secondary.main} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <StatCard title="Total Services" value={totalServices} icon={<MiscellaneousServices />} color={colors.primary.main} />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Profile Card */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                    <Avatar src={currentLogo} sx={{ width: 120, height: 120, border: `3px solid ${colors.grey[200]}` }} />
                    <IconButton onClick={() => { setLogoPreview(''); setLogoDialogOpen(true); }} size="small"
                      sx={{ position: 'absolute', bottom: 0, right: 0, bgcolor: colors.primary.main, color: 'white', '&:hover': { bgcolor: colors.primary.dark } }}>
                      <CameraAlt fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>{profile?.name}</Typography>
                  <Chip label={profile?.code} size="small" sx={{ bgcolor: colors.primary.main, color: 'white', fontWeight: 600 }} />
                  {settings?.tagline && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                      "{settings.tagline}"
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <LocationOn sx={{ color: colors.grey[500], fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">{details?.office_location || 'Not set'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Phone sx={{ color: colors.grey[500], fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">{details?.phone || 'Not set'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Email sx={{ color: colors.grey[500], fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">{details?.email || 'Not set'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Language sx={{ color: colors.grey[500], fontSize: 20 }} />
                    {details?.website ? (
                      <Typography variant="body2" component="a" href={details.website} target="_blank"
                        sx={{ color: colors.primary.main, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                        {details.website}
                      </Typography>
                    ) : <Typography variant="body2" color="text.secondary">Not set</Typography>}
                  </Box>
                </Box>

                {/* Social Media */}
                {(settings?.facebook || settings?.twitter || settings?.instagram || settings?.linkedin || settings?.youtube) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                      {settings.facebook && <IconButton size="small" href={settings.facebook} target="_blank" sx={{ color: '#1877F2' }}><Facebook /></IconButton>}
                      {settings.twitter && <IconButton size="small" href={settings.twitter} target="_blank" sx={{ color: '#000' }}><XIcon /></IconButton>}
                      {settings.instagram && <IconButton size="small" href={settings.instagram} target="_blank" sx={{ color: '#E4405F' }}><Instagram /></IconButton>}
                      {settings.linkedin && <IconButton size="small" href={settings.linkedin} target="_blank" sx={{ color: '#0A66C2' }}><LinkedIn /></IconButton>}
                      {settings.youtube && <IconButton size="small" href={settings.youtube} target="_blank" sx={{ color: '#FF0000' }}><YouTube /></IconButton>}
                    </Box>
                  </>
                )}

                <Divider sx={{ my: 2 }} />

                {can('profile.edit') && <Button fullWidth variant="contained" startIcon={<Edit />} onClick={handleEditProfile}>
                  Edit Profile
                </Button>}
              </CardContent>
            </Card>
          </Grid>

          {/* Main Content */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
                  <Tab icon={<Edit />} iconPosition="start" label="Signatories" />
                  <Tab icon={<Settings />} iconPosition="start" label="Configurations" />
                  <Tab icon={<QuestionAnswer />} iconPosition="start" label="FAQs" />
                  <Tab icon={<ContactPhone />} iconPosition="start" label="Contacts" />
                  <Tab icon={<ViewModule />} iconPosition="start" label="Modules Overview" />
                  <Tab icon={<Receipt />} iconPosition="start" label="Billing" />
                </Tabs>
              </Box>

              <CardContent>
                {/* Signatories Tab */}
                <TabPanel value={tabValue} index={0}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6">Document Signatories</Typography>
                    {can('profile.manage_signatories') && <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenSignatoryDialog()}>
                      Add Signatory
                    </Button>}
                  </Box>

                  {signatories && Array.isArray(signatories) && signatories.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell>Signature</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {signatories.map((s: CountySignatory) => (
                            <TableRow key={s.id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                              </TableCell>
                              <TableCell>{s.title}</TableCell>
                              <TableCell>
                                {s.signature ? (
                                  <Box component="img" src={s.signature} alt={`${s.name} signature`}
                                    sx={{ height: 40, maxWidth: 120, objectFit: 'contain' }} />
                                ) : (
                                  <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => handleUploadSignature(s)}>Upload</Button>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip label={s.is_active ? 'Active' : 'Inactive'} size="small"
                                  color={s.is_active ? 'success' : 'default'} />
                              </TableCell>
                              <TableCell align="right">
                                <IconButton size="small" sx={{ color: colors.primary.main }} onClick={() => handleOpenSignatoryDialog(s)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => handleDeleteSignatory(s.id)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <Edit sx={{ fontSize: 64, color: colors.grey[400], mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>No Signatories Added</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Add signatories whose signatures will appear on printed documents
                      </Typography>
                      {can('profile.manage_signatories') && <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenSignatoryDialog()}>Add Signatory</Button>}
                    </Box>
                  )}
                </TabPanel>

                {/* Configurations Tab */}
                <TabPanel value={tabValue} index={1}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>System Configurations</Typography>

                  {/* Maker-Checker */}
                  <Card sx={{ mb: 3, border: `2px solid ${makerCheckerEnabled ? colors.success.main : colors.warning.main}` }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ flex: 1, minWidth: 250 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Security sx={{ color: makerCheckerEnabled ? colors.success.main : colors.warning.main }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>Maker-Checker Approval System</Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {makerCheckerEnabled
                              ? 'All critical changes require approval from a different user before being applied.'
                              : 'WARNING: Changes are applied immediately without approval. Only disable for testing.'}
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={<Switch checked={makerCheckerEnabled}
                            onChange={(e) => { setMakerCheckerEnabled(e.target.checked); toggleMakerCheckerMutation.mutate(e.target.checked); }}
                            disabled={toggleMakerCheckerMutation.isPending} />}
                          label={makerCheckerEnabled ? 'Enabled' : 'Disabled'} labelPlacement="start" sx={{ m: 0 }}
                        />
                      </Box>
                      {!makerCheckerEnabled && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          <strong>Testing Mode Active:</strong> All changes will be applied immediately.
                        </Alert>
                      )}
                    </CardContent>
                  </Card>

                  {/* Theme Color Preview */}
                  {settings?.theme_color && (
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: settings.theme_color, border: `1px solid ${colors.grey[300]}` }} />
                          <Box>
                            <Typography variant="subtitle2">Theme Color</Typography>
                            <Typography variant="body2" color="text.secondary">{settings.theme_color}</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                </TabPanel>

                {/* FAQs Tab */}
                <TabPanel value={tabValue} index={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6">Frequently Asked Questions</Typography>
                    {can('profile.edit') && (
                      <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenFaqDialog()}>
                        Add FAQ
                      </Button>
                    )}
                  </Box>

                  {faqsLoading ? (
                    <Box display="flex" justifyContent="center" py={6}><CircularProgress size={32} /></Box>
                  ) : faqs && faqs.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, width: '35%' }}>Question</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Answer</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {faqs.map((f: Faq) => (
                            <TableRow key={f.id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{f.question}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{f.answer}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                {can('profile.edit') && (
                                  <Tooltip title="Edit">
                                    <IconButton size="small" sx={{ color: colors.primary.main }} onClick={() => handleOpenFaqDialog(f)}>
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {can('profile.edit') && (
                                  <Tooltip title="Delete">
                                    <IconButton size="small" color="error" onClick={() => handleDeleteFaq(f.id)}>
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <QuestionAnswer sx={{ fontSize: 64, color: colors.grey[400], mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>No FAQs Added</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Add frequently asked questions to help your residents
                      </Typography>
                      {can('profile.edit') && (
                        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenFaqDialog()}>Add FAQ</Button>
                      )}
                    </Box>
                  )}
                </TabPanel>

                {/* Contacts Tab */}
                <TabPanel value={tabValue} index={3}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6">Contact Channels</Typography>
                    {can('profile.edit') && (
                      <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenContactDialog()}>
                        Add Contact
                      </Button>
                    )}
                  </Box>

                  {contactsLoading ? (
                    <Box display="flex" justifyContent="center" py={6}><CircularProgress size={32} /></Box>
                  ) : contacts && contacts.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Channel</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {contacts.map((c: CountyContact) => (
                            <TableRow key={c.id}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  {c.contact?.logo && (
                                    <Avatar src={c.contact.logo} sx={{ width: 28, height: 28 }} variant="rounded" />
                                  )}
                                  <Typography variant="body2" fontWeight={600}>{c.contact?.name || `#${c.contact_id}`}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">{c.value}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                {can('profile.edit') && (
                                  <Tooltip title="Edit">
                                    <IconButton size="small" sx={{ color: colors.primary.main }} onClick={() => handleOpenContactDialog(c)}>
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {can('profile.edit') && (
                                  <Tooltip title="Delete">
                                    <IconButton size="small" color="error" onClick={() => handleDeleteContact(c.id)}>
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <ContactPhone sx={{ fontSize: 64, color: colors.grey[400], mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>No Contacts Added</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Add contact channels (e.g., Email, Facebook, Twitter) for your residents
                      </Typography>
                      {can('profile.edit') && (
                        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenContactDialog()}>Add Contact</Button>
                      )}
                    </Box>
                  )}
                </TabPanel>

                {/* Modules Overview Tab */}
                <TabPanel value={tabValue} index={4}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>Modules & Services</Typography>
                  {Array.isArray(modules) && modules.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="center">Services</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {modules.map((m: any) => (
                            <TableRow key={m.id} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{m.name}</Typography>
                                {m.module_type?.name && (
                                  <Typography variant="caption" color="text.secondary">{m.module_type.name}</Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={m.services_count ?? m.services?.length ?? 0} size="small" color="primary" />
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={m.status || (m.is_active ? 'Active' : 'Inactive')} size="small"
                                  color={m.status === 'active' || m.is_active ? 'success' : 'default'} sx={{ textTransform: 'capitalize' }} />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <ViewModule sx={{ fontSize: 64, color: colors.grey[400], mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>No Modules Configured</Typography>
                      <Typography variant="body2" color="text.secondary">Modules will appear here once configured</Typography>
                    </Box>
                  )}
                </TabPanel>

                {/* Billing Tab */}
                <TabPanel value={tabValue} index={5}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>Billing Configuration</Typography>

                  {billing.isLoading ? (
                    <Box display="flex" justifyContent="center" py={6}><CircularProgress size={32} /></Box>
                  ) : !billing.config || billing.status === 'no_config' ? (
                    <Alert severity="info">
                      No billing plan has been configured for this county yet. Contact the system provider to set one up.
                    </Alert>
                  ) : (
                    <>
                      {/* Status banner */}
                      {(() => {
                        const s = billing.status;
                        const dr = billing.daysRemaining;
                        if (s === 'expired') {
                          return (
                            <Alert severity="error" icon={<EventBusy />} sx={{ mb: 3 }}>
                              <strong>Subscription expired.</strong> Some features are restricted until renewal. Contact the system provider to renew.
                            </Alert>
                          );
                        }
                        if (s === 'suspended') {
                          return (
                            <Alert severity="error" sx={{ mb: 3 }}>
                              <strong>Billing suspended.</strong> All county services are currently blocked.
                            </Alert>
                          );
                        }
                        if (s === 'due_today') {
                          return (
                            <Alert severity="warning" sx={{ mb: 3 }}>
                              <strong>Renewal is due today.</strong> Please coordinate payment to avoid service interruption.
                            </Alert>
                          );
                        }
                        if (s === 'expiring_soon') {
                          return (
                            <Alert severity="warning" sx={{ mb: 3 }}>
                              <strong>Renewal due in {dr} day{dr === 1 ? '' : 's'}.</strong> Reminders are being sent to county admins.
                            </Alert>
                          );
                        }
                        return (
                          <Alert severity="success" icon={<EventAvailable />} sx={{ mb: 3 }}>
                            Billing is active{dr ? ` — ${dr} day${dr === 1 ? '' : 's'} until next renewal` : ''}.
                          </Alert>
                        );
                      })()}

                      <TableContainer component={Paper} variant="outlined">
                        <Table>
                          <TableBody>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, width: 220, color: 'text.secondary' }}>Plan Type</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                                  <Chip
                                    label={(billing.config.type || '').replace('_', ' ')}
                                    size="small"
                                    sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                                    color={billing.config.type === 'subscription' ? 'primary' : billing.config.type === 'service_fee' ? 'info' : 'success'}
                                  />
                                  {billing.isTrial && (
                                    <Chip
                                      icon={<Receipt sx={{ fontSize: '14px !important' }} />}
                                      label="Free Trial"
                                      size="small"
                                      color="success"
                                      sx={{ fontWeight: 700 }}
                                    />
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
                              <TableCell>
                                <Chip
                                  label={billing.status?.replace('_', ' ')}
                                  size="small"
                                  sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                                  color={
                                    billing.status === 'active' ? 'success' :
                                    billing.status === 'expiring_soon' || billing.status === 'due_today' ? 'warning' :
                                    billing.status === 'expired' || billing.status === 'suspended' ? 'error' :
                                    'default'
                                  }
                                />
                              </TableCell>
                            </TableRow>

                            {billing.config.type === 'subscription' && (
                              <>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Start Date</TableCell>
                                  <TableCell>{billing.config.starts_at ? new Date(billing.config.starts_at).toLocaleDateString() : '—'}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>End Date</TableCell>
                                  <TableCell>
                                    {billing.config.ends_at ? new Date(billing.config.ends_at).toLocaleDateString() : '—'}
                                    {billing.daysRemaining !== null && billing.daysRemaining >= 0 && (
                                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                        ({billing.daysRemaining} day{billing.daysRemaining === 1 ? '' : 's'} remaining)
                                      </Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Subscription Amount</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    {billing.config.subscription_currency || 'KES'} {Number(billing.config.subscription_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                  </TableCell>
                                </TableRow>
                              </>
                            )}

                            {billing.config.type === 'service_fee' && (
                              <>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Fee Mode</TableCell>
                                  <TableCell sx={{ textTransform: 'capitalize' }}>{billing.config.service_fee_mode || '—'}</TableCell>
                                </TableRow>
                                {billing.config.service_fee_mode === 'percentage' && (
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Fee Percentage</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{billing.config.service_fee_percentage}%</TableCell>
                                  </TableRow>
                                )}
                                {billing.config.service_fee_mode === 'tiered' && billing.config.service_fee_tiers && (
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Tiers</TableCell>
                                    <TableCell>
                                      {billing.config.service_fee_tiers.map((t, i) => (
                                        <Typography key={i} variant="body2">
                                          {t.min} – {t.max === null ? '∞' : t.max}: <strong>KES {Number(t.fee).toLocaleString()}</strong>
                                        </Typography>
                                      ))}
                                    </TableCell>
                                  </TableRow>
                                )}
                              </>
                            )}

                            {billing.config.type === 'revenue_share' && (
                              <>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Provider Share</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>{billing.config.revenue_share_provider_percentage}%</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>County Share</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>{billing.config.revenue_share_county_percentage}%</TableCell>
                                </TableRow>
                              </>
                            )}

                            {billing.config.notes && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', verticalAlign: 'top' }}>Notes</TableCell>
                                <TableCell sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>{billing.config.notes}</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                        Billing is configured and managed by the system provider. Reach out to them to make changes.
                      </Typography>

                      {/* Settlements panel: only relevant for service_fee / revenue_share plans */}
                      {(billing.config.type === 'service_fee' || billing.config.type === 'revenue_share') && (
                        <CountySettlementsPanel billingType={billing.config.type} />
                      )}
                    </>
                  )}
                </TabPanel>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ==================== EDIT PROFILE DIALOG (merged) ==================== */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit County Profile</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Typography variant="overline" color="text.secondary">Contact Information</Typography>
              <TextField fullWidth label="County Name" value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} />
              <TextField fullWidth label="Address" value={profileForm.address}
                onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })} />
              <TextField fullWidth label="Phone" value={profileForm.phone}
                onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} />
              <TextField fullWidth label="Email" type="email" value={profileForm.email}
                onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })} />
              <TextField fullWidth label="Website" value={profileForm.website} placeholder="https://..."
                onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })} />

              <Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">Branding</Typography></Divider>
              <TextField fullWidth label="Tagline" value={profileForm.tagline} placeholder="e.g., The County of Opportunities"
                onChange={(e) => setProfileForm({ ...profileForm, tagline: e.target.value })} />
              <TextField fullWidth label="Theme Color" type="color" value={profileForm.theme_color || '#000000'}
                onChange={(e) => setProfileForm({ ...profileForm, theme_color: e.target.value })} InputLabelProps={{ shrink: true }} />

              <Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">Content</Typography></Divider>
              <TextField fullWidth label="Business Hours" value={profileForm.business_hours} multiline minRows={2}
                placeholder="e.g., Mon–Fri: 8:00 AM – 5:00 PM"
                onChange={(e) => setProfileForm({ ...profileForm, business_hours: e.target.value })} />
              <TextField fullWidth label="About" value={profileForm.about} multiline minRows={3}
                placeholder="A short description of the county..."
                onChange={(e) => setProfileForm({ ...profileForm, about: e.target.value })} />
              <TextField fullWidth label="Footer Blurb" value={profileForm.footer_blurb} multiline minRows={2}
                placeholder="Text shown in the public portal footer"
                onChange={(e) => setProfileForm({ ...profileForm, footer_blurb: e.target.value })} />

              <Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">Social Media</Typography></Divider>
              <TextField fullWidth label="Facebook" placeholder="https://facebook.com/your-county" value={profileForm.facebook}
                onChange={(e) => setProfileForm({ ...profileForm, facebook: e.target.value })}
                slotProps={{ input: { startAdornment: <InputAdornment position="start"><Facebook sx={{ color: '#1877F2' }} /></InputAdornment> } }} />
              <TextField fullWidth label="X (formerly Twitter)" placeholder="https://x.com/your-county" value={profileForm.twitter}
                onChange={(e) => setProfileForm({ ...profileForm, twitter: e.target.value })}
                slotProps={{ input: { startAdornment: <InputAdornment position="start"><XIcon sx={{ color: '#000' }} /></InputAdornment> } }} />
              <TextField fullWidth label="Instagram" placeholder="https://instagram.com/your-county" value={profileForm.instagram}
                onChange={(e) => setProfileForm({ ...profileForm, instagram: e.target.value })}
                slotProps={{ input: { startAdornment: <InputAdornment position="start"><Instagram sx={{ color: '#E4405F' }} /></InputAdornment> } }} />
              <TextField fullWidth label="LinkedIn" placeholder="https://linkedin.com/company/your-county" value={profileForm.linkedin}
                onChange={(e) => setProfileForm({ ...profileForm, linkedin: e.target.value })}
                slotProps={{ input: { startAdornment: <InputAdornment position="start"><LinkedIn sx={{ color: '#0A66C2' }} /></InputAdornment> } }} />
              <TextField fullWidth label="YouTube" placeholder="https://youtube.com/@your-county" value={profileForm.youtube}
                onChange={(e) => setProfileForm({ ...profileForm, youtube: e.target.value })}
                slotProps={{ input: { startAdornment: <InputAdornment position="start"><YouTube sx={{ color: '#FF0000' }} /></InputAdornment> } }} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)} startIcon={<Cancel />}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveProfile}
              disabled={updateProfileMutation.isPending || updateSettingsMutation.isPending || JSON.stringify(profileForm) === JSON.stringify(originalProfileForm)}
              startIcon={(updateProfileMutation.isPending || updateSettingsMutation.isPending) ? <CircularProgress size={20} /> : <Save />}>
              {(updateProfileMutation.isPending || updateSettingsMutation.isPending) ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ==================== FAQ DIALOG (create + edit) ==================== */}
        <Dialog open={faqDialogOpen} onClose={() => { setFaqDialogOpen(false); resetFaqForm(); }} maxWidth="sm" fullWidth>
          <DialogTitle>{editingFaq ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField fullWidth label="Question *" required value={faqForm.question}
                onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })} />
              <TextField fullWidth label="Answer *" required multiline minRows={4} value={faqForm.answer}
                onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setFaqDialogOpen(false); resetFaqForm(); }} startIcon={<Cancel />}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveFaq}
              disabled={addFaqMutation.isPending || updateFaqMutation.isPending || !faqForm.question.trim() || !faqForm.answer.trim()}
              startIcon={(addFaqMutation.isPending || updateFaqMutation.isPending) ? <CircularProgress size={20} /> : <Save />}>
              {(addFaqMutation.isPending || updateFaqMutation.isPending) ? 'Saving...' : editingFaq ? 'Update' : 'Add FAQ'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ==================== CONTACT DIALOG (create + edit) ==================== */}
        <Dialog open={contactDialogOpen} onClose={() => { setContactDialogOpen(false); resetContactForm(); }} maxWidth="sm" fullWidth>
          <DialogTitle>{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth required>
                <InputLabel id="contact-type-label">Channel</InputLabel>
                <Select
                  labelId="contact-type-label"
                  label="Channel"
                  value={contactForm.contact_id === '' ? '' : String(contactForm.contact_id)}
                  onChange={(e) => setContactForm({ ...contactForm, contact_id: e.target.value === '' ? '' : Number(e.target.value) })}
                >
                  {(contactTypes || []).map((t: ContactType) => (
                    <MenuItem key={t.id} value={String(t.id)}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {t.logo && <Avatar src={t.logo} sx={{ width: 24, height: 24 }} variant="rounded" />}
                        {t.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField fullWidth label="Value *" required value={contactForm.value}
                placeholder="e.g., info@county.go.ke or https://facebook.com/county"
                onChange={(e) => setContactForm({ ...contactForm, value: e.target.value })} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setContactDialogOpen(false); resetContactForm(); }} startIcon={<Cancel />}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveContact}
              disabled={addContactMutation.isPending || updateContactMutation.isPending || contactForm.contact_id === '' || !contactForm.value.trim()}
              startIcon={(addContactMutation.isPending || updateContactMutation.isPending) ? <CircularProgress size={20} /> : <Save />}>
              {(addContactMutation.isPending || updateContactMutation.isPending) ? 'Saving...' : editingContact ? 'Update' : 'Add Contact'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ==================== SIGNATORY DIALOG (create + edit) ==================== */}
        <Dialog open={signatoryDialogOpen} onClose={() => { setSignatoryDialogOpen(false); resetSignatoryForm(); }} maxWidth="md" fullWidth>
          <DialogTitle>{editingSignatory ? 'Edit Signatory' : 'Add Signatory'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField fullWidth label="Name *" required value={signatoryForm.name}
                  onChange={(e) => setSignatoryForm({ ...signatoryForm, name: e.target.value })} />
                <TextField fullWidth label="Title *" required placeholder="e.g., County Executive, Finance Officer"
                  value={signatoryForm.title} onChange={(e) => setSignatoryForm({ ...signatoryForm, title: e.target.value })} />
              </Box>

              {!editingSignatory && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Signature *</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <ToggleButtonGroup value={signatureMode} exclusive onChange={(_, m) => m && setSignatureMode(m)} size="small">
                        <ToggleButton value="upload"><Upload sx={{ mr: 0.5, fontSize: 18 }} />Upload</ToggleButton>
                        <ToggleButton value="draw"><Draw sx={{ mr: 0.5, fontSize: 18 }} />Draw</ToggleButton>
                        <ToggleButton value="type"><TextFields sx={{ mr: 0.5, fontSize: 18 }} />Type</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {signatureMode === 'upload' && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                        {logoPreview && <Box component="img" src={logoPreview} alt="Preview" sx={{ maxHeight: 100, maxWidth: 350, objectFit: 'contain', border: `1px solid ${colors.grey[300]}`, p: 2, bgcolor: 'white' }} />}
                        <Button variant="outlined" component="label" startIcon={<Upload />} size="small">
                          Choose Image<input type="file" hidden accept="image/*" onChange={handleLogoChange} />
                        </Button>
                      </Box>
                    )}

                    {signatureMode === 'draw' && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                        <Box sx={{ border: `2px dashed ${colors.grey[400]}`, borderRadius: 1, bgcolor: 'white', cursor: 'crosshair' }}>
                          <canvas ref={(el) => { canvasRef[1](el); if (el) { const ctx = el.getContext('2d'); if (ctx) { ctx.fillStyle = 'white'; ctx.fillRect(0, 0, el.width, el.height); } } }}
                            width={450} height={120} onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} />
                        </Box>
                        <Button variant="text" startIcon={<Clear />} onClick={handleClearCanvas} size="small">Clear</Button>
                      </Box>
                    )}

                    {signatureMode === 'type' && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField fullWidth size="small" label="Type your name" value={signatureText} onChange={(e) => setSignatureText(e.target.value)} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" gutterBottom display="block">Font Style:</Typography>
                          <ToggleButtonGroup value={signatureFont} exclusive onChange={(_, f) => f && setSignatureFont(f)} size="small">
                            <ToggleButton value="Brush Script MT" sx={{ fontFamily: 'Brush Script MT, cursive', fontSize: 12 }}>Brush</ToggleButton>
                            <ToggleButton value="Lucida Handwriting" sx={{ fontFamily: 'Lucida Handwriting, cursive', fontSize: 12 }}>Handwriting</ToggleButton>
                            <ToggleButton value="Segoe Script" sx={{ fontFamily: 'Segoe Script, cursive', fontSize: 12 }}>Script</ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                        {signatureText && (
                          <Box sx={{ border: `1px solid ${colors.grey[300]}`, borderRadius: 1, p: 2, bgcolor: 'white', textAlign: 'center' }}>
                            <Typography variant="h5" sx={{ fontFamily: `${signatureFont}, cursive`, color: 'black' }}>{signatureText}</Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setSignatoryDialogOpen(false); resetSignatoryForm(); }}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveSignatory}
              disabled={(addSignatoryMutation.isPending || updateSignatoryMutation.isPending) || !signatoryForm.name || !signatoryForm.title ||
                (!editingSignatory && signatureMode === 'upload' && !logoPreview) || (!editingSignatory && signatureMode === 'type' && !signatureText)}
              startIcon={(addSignatoryMutation.isPending || updateSignatoryMutation.isPending) ? <CircularProgress size={20} /> : <Save />}>
              {(addSignatoryMutation.isPending || updateSignatoryMutation.isPending) ? 'Saving...' : editingSignatory ? 'Update' : 'Add Signatory'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ==================== SIGNATURE UPLOAD DIALOG ==================== */}
        <Dialog open={signatureDialogOpen} onClose={() => setSignatureDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Add Signature</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Adding signature for: <strong>{selectedSignatory?.name}</strong> ({selectedSignatory?.title})
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <ToggleButtonGroup value={signatureMode} exclusive onChange={(_, m) => m && setSignatureMode(m)}>
                  <ToggleButton value="upload"><Upload sx={{ mr: 1 }} />Upload</ToggleButton>
                  <ToggleButton value="draw"><Draw sx={{ mr: 1 }} />Draw</ToggleButton>
                  <ToggleButton value="type"><TextFields sx={{ mr: 1 }} />Type</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {signatureMode === 'upload' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                  {logoPreview && <Box component="img" src={logoPreview} alt="Preview" sx={{ maxHeight: 120, maxWidth: 400, objectFit: 'contain', border: `1px solid ${colors.grey[300]}`, p: 2, bgcolor: 'white' }} />}
                  <Button variant="outlined" component="label" startIcon={<Upload />}>
                    Choose Image<input type="file" hidden accept="image/*" onChange={handleLogoChange} />
                  </Button>
                  <Typography variant="caption" color="text.secondary">Transparent background recommended</Typography>
                </Box>
              )}

              {signatureMode === 'draw' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                  <Box sx={{ border: `2px dashed ${colors.grey[400]}`, borderRadius: 1, bgcolor: 'white', cursor: 'crosshair' }}>
                    <canvas ref={(el) => { canvasRef[1](el); if (el) { const ctx = el.getContext('2d'); if (ctx) { ctx.fillStyle = 'white'; ctx.fillRect(0, 0, el.width, el.height); } } }}
                      width={500} height={150} onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} />
                  </Box>
                  <Button variant="outlined" startIcon={<Clear />} onClick={handleClearCanvas} size="small">Clear</Button>
                </Box>
              )}

              {signatureMode === 'type' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField fullWidth label="Type your signature" value={signatureText} onChange={(e) => setSignatureText(e.target.value)} />
                  <ToggleButtonGroup value={signatureFont} exclusive onChange={(_, f) => f && setSignatureFont(f)} size="small">
                    <ToggleButton value="Brush Script MT" sx={{ fontFamily: 'Brush Script MT, cursive' }}>Brush</ToggleButton>
                    <ToggleButton value="Lucida Handwriting" sx={{ fontFamily: 'Lucida Handwriting, cursive' }}>Handwriting</ToggleButton>
                    <ToggleButton value="Segoe Script" sx={{ fontFamily: 'Segoe Script, cursive' }}>Script</ToggleButton>
                  </ToggleButtonGroup>
                  {signatureText && (
                    <Box sx={{ border: `1px solid ${colors.grey[300]}`, borderRadius: 1, p: 3, bgcolor: 'white', textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ fontFamily: `${signatureFont}, cursive`, color: 'black' }}>{signatureText}</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSignatureDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveSignature}
              disabled={uploadSignatureMutation.isPending || (signatureMode === 'upload' && !logoPreview) || (signatureMode === 'type' && !signatureText)}
              startIcon={uploadSignatureMutation.isPending ? <CircularProgress size={20} /> : <Save />}>
              {uploadSignatureMutation.isPending ? 'Saving...' : 'Save Signature'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ==================== LOGO DIALOG ==================== */}
        <Dialog open={logoDialogOpen} onClose={() => setLogoDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Update County Logo</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">Current Logo</Typography>
              <Avatar src={currentLogo} sx={{ width: 100, height: 100, border: `3px solid ${colors.grey[200]}` }} />
              {logoPreview && (
                <>
                  <Typography variant="caption" color="text.secondary">New Logo Preview</Typography>
                  <Avatar src={logoPreview} sx={{ width: 100, height: 100, border: `3px solid ${colors.success.main}` }} />
                </>
              )}
              <Button variant="outlined" component="label" startIcon={<CameraAlt />}>
                Choose Logo<input type="file" hidden accept="image/*" onChange={handleLogoChange} />
              </Button>
              <Typography variant="caption" color="text.secondary">Supported: JPG, PNG, SVG</Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLogoDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => { if (logoPreview) updateLogoMutation.mutate(logoPreview); }}
              disabled={updateLogoMutation.isPending || !logoPreview}
              startIcon={updateLogoMutation.isPending ? <CircularProgress size={20} /> : <Save />}>
              {updateLogoMutation.isPending ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
